'use client';

import * as React from 'react';
import { Download } from 'lucide-react';
import { useTranslations } from 'next-intl';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogOverlay,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { cn } from '@/lib/utils';
import { MarkdownContent } from '@/lib/markdown/MarkdownContent';
import { Alert, AlertDescription } from '@/components/ui/Alert';
import { useDocumentsStore } from './documentsStore';
import { isConversionEmpty } from './documentStatus';

// Content types we render as markdown/plain text directly from `content`. Anything
// else (a raw uploaded PDF, or a rendered xlsx/pptx/docx analytics artifact) is
// either previewed via its converted `markdown` (below) or offered as a download.
function isTextual(contentType: string): boolean {
  if (!contentType) return true; // unknown ⇒ assume text (saved reports, notes)
  return /^text\//.test(contentType) || contentType.includes('markdown');
}

/**
 * Read-only viewer for an uploaded library document. Opened from a row click or a
 * /library?id=… deep-link; content is fetched on demand via openDocumentViewer
 * (GetDocumentContent), which returns the original bytes AND — for a converted
 * binary source doc (PDF/DOCX) — the persisted `markdown`.
 *
 * Rendering:
 *   - text-native docs (.md/.txt/reports): render `content` as markdown.
 *   - converted binary docs (PDF/DOCX with `markdown`): a Markdown / Original tab
 *     toggle — markdown by default, the original PDF inline-embedded, both
 *     downloadable.
 *   - rendered artifacts / anything else: graceful fallback + download.
 */
export function DocumentViewerModal() {
  const t = useTranslations('documents');
  const docId = useDocumentsStore((s) => s.viewerDocId);
  const content = useDocumentsStore((s) => s.viewerContent);
  const loading = useDocumentsStore((s) => s.viewerLoading);
  const close = useDocumentsStore((s) => s.actions.closeDocumentViewer);
  const documents = useDocumentsStore((s) => s.documents);

  const doc = docId ? documents.find((d) => d.documentId === docId) : undefined;
  const title = doc?.title || doc?.filename || t('documentViewerModal.defaultTitle');

  const open = docId !== null;
  const hasMarkdown = !!content?.markdown?.trim();
  const textual = content ? isTextual(content.contentType) : true;
  const isPdf = content?.contentType === 'application/pdf';

  // The casing trap lives in `documentStatus.ts`, not here: this file used to
  // spell the wire value out again as a lowercase literal, which is one of the
  // three independent spellings Stage 6c found.
  const conversionEmpty = isConversionEmpty(doc?.indexingStatus);

  // Tab is only meaningful for a converted binary doc (has both markdown + bytes).
  const [tab, setTab] = React.useState<'markdown' | 'original'>('markdown');
  // Reset to the markdown tab whenever a new document opens.
  React.useEffect(() => {
    setTab('markdown');
  }, [docId]);

  // Object URL for inline PDF preview (original tab). Built lazily from the raw
  // bytes and revoked on change/unmount so we never leak blobs.
  const pdfUrl = React.useMemo(() => {
    if (!content || !isPdf) return null;
    return URL.createObjectURL(
      new Blob([new Uint8Array(content.bytes)], { type: 'application/pdf' }),
    );
  }, [content, isPdf]);
  React.useEffect(() => {
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
  }, [pdfUrl]);

  // Build a Blob from the RAW bytes (never the TextDecoder string — that mangles
  // binary) and trigger a browser download under the document's filename.
  const handleDownload = React.useCallback(() => {
    if (!content) return;
    const blob = new Blob([new Uint8Array(content.bytes)], {
      type: content.contentType || 'application/octet-stream',
    });
    const url = URL.createObjectURL(blob);
    try {
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = doc?.filename || `${title}` || 'document';
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
    } finally {
      URL.revokeObjectURL(url);
    }
  }, [content, doc, title]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogOverlay onClick={close} />
      <DialogContent className="max-h-[85vh] w-full max-w-3xl overflow-hidden">
        <DialogHeader>
          <div className="flex items-center justify-between gap-3 pr-8">
            <DialogTitle className="truncate">{title}</DialogTitle>
            {content && (
              <Button
                variant="secondary"
                size="sm"
                onClick={handleDownload}
                className="shrink-0 gap-1.5"
              >
                <Download className="h-4 w-4" aria-hidden />
                {t('documentViewerModal.download')}
              </Button>
            )}
          </div>
          {/* Markdown / Original toggle — only for a converted binary doc. */}
          {content && hasMarkdown && !textual && (
            <div className="mt-2 flex gap-1" role="tablist" aria-label={t('documentViewerModal.tabsAria')}>
              {(['markdown', 'original'] as const).map((viewTab) => (
                <button
                  key={viewTab}
                  type="button"
                  role="tab"
                  aria-selected={tab === viewTab}
                  onClick={() => setTab(viewTab)}
                  className={cn(
                    'rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
                    tab === viewTab
                      ? 'bg-accent/15 text-accent'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  {viewTab === 'markdown'
                    ? t('documentViewerModal.tabMarkdown')
                    : isPdf
                      ? t('documentViewerModal.tabOriginalPdf')
                      : t('documentViewerModal.tabOriginal')}
                </button>
              ))}
            </div>
          )}
        </DialogHeader>

        <div className="max-h-[70vh] overflow-auto">
          {loading && (
            <div className="space-y-2 py-2">
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-1/2" />
            </div>
          )}

          {!loading && content && (
            <>
              {/* Text-native doc: render content directly (no tabs). */}
              {textual ? (
                content.content.trim() ? (
                  <MarkdownContent content={content.content} />
                ) : (
                  <p className="py-6 text-center text-sm text-muted-foreground">
                    {t('documentViewerModal.emptyDocument')}
                  </p>
                )
              ) : hasMarkdown ? (
                // Converted binary doc: markdown preview OR the original.
                tab === 'markdown' ? (
                  <>
                    {/* BE-15. hasMarkdown is tested BEFORE conversionEmpty in this
                        chain, so a document whose latest conversion produced
                        nothing but which still holds an earlier conversion's text
                        already rendered that text here — silently, beside a list
                        badge whose label denied any text existed. (BE-15 reworded
                        that label in the same change, so this comment names the
                        behaviour rather than quoting the string — a comment that
                        quotes a value it does not own goes stale on the commit
                        that changes it.) The text is kept deliberately
                        (persistIndexerFields cannot clear Markdown; clearing it
                        would destroy content the user paid Docling credits for on
                        a transient blip), so what was missing was the disclosure.

                        A STATIC string, never doc.failureReason: the reason is
                        computed once at conversion time and persisted, so every
                        document already in this state carries the older sentence.
                        Rendering it here would promote stale copy into a prominent
                        warning. Which conversion you are reading is a property of
                        this surface, not of the document.

                        Markdown tab only — the Original tab shows the source file,
                        which is not "text from an earlier conversion". */}
                    {conversionEmpty && (
                      <Alert variant="warning" className="mb-3">
                        <AlertDescription>
                          {t('documentViewerModal.previousConversionShown')}
                        </AlertDescription>
                      </Alert>
                    )}
                    <MarkdownContent content={content.markdown} />
                  </>
                ) : isPdf && pdfUrl ? (
                  <embed
                    src={pdfUrl}
                    type="application/pdf"
                    className="h-[68vh] w-full rounded-md border border-border"
                    aria-label={t('documentViewerModal.pdfEmbedAria', { title })}
                  />
                ) : (
                  <p className="py-6 text-center text-sm text-muted-foreground">
                    {t('documentViewerModal.cannotPreview', { contentType: content.contentType })}
                  </p>
                )
              ) : conversionEmpty ? (
                // A conversion that COMPLETED and produced no text. Until BE-11
                // this rendered cannotPreview, which blames the FILE TYPE for
                // what is actually an empty conversion — the comment below has
                // named this case since the branch was written, and the copy
                // still pointed at the wrong cause. The document's own
                // failureReason already says what happened and what to do.
                //
                // NOTE for the next person: `cannotPreview` renders at the two
                // sites BELOW and above this branch (the non-PDF binary fallback
                // and the non-previewable textual case) — NOT here, and neither
                // of them is reachable for conversionEmpty, because this branch
                // intercepts it first. So do not "fix" cannotPreview when you
                // are chasing an empty-conversion report; you would be editing
                // copy this status never reaches.
                //
                // (The first draft of this comment claimed cannotPreview rendered
                // in "the textual one above and this one". All three clauses were
                // wrong: the textual branch renders emptyDocument, this branch
                // renders neither, and the real sites are elsewhere. Caught in
                // review — a comment written to prevent a mistake, making one.)
                <p className="py-6 text-center text-sm text-warning">
                  {doc?.failureReason || t('documentViewerModal.conversionEmpty')}
                </p>
              ) : (
                // Binary doc with no converted text (rendered artifact, or a
                // conversion that produced nothing): download-only fallback.
                <p className="py-6 text-center text-sm text-muted-foreground">
                  {t('documentViewerModal.cannotPreview', { contentType: content.contentType })}
                </p>
              )}
            </>
          )}

          {!loading && !content && (
            <p className="py-6 text-center text-sm text-muted-foreground">
              {t('documentViewerModal.loadError')}
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
